import { useState, useEffect, useRef } from 'react';
import { X, Send, AlertTriangle, Shield, Bug, FileText, MessageSquare, User, Clock } from 'lucide-react';

/**
 * AdminFeedbackPanel - Slide-out panel for admins to view and send feedback
 * Admins can view the full conversation thread and send additional messages
 */
const AdminFeedbackPanel = ({ listingId, listingTitle, isOpen, onClose, accessToken }) => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('general');
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  // Feedback type icons and labels
  const feedbackTypeConfig = {
    security_issue: { icon: Shield, label: 'Security Issue', color: 'text-red-400' },
    malware_concern: { icon: AlertTriangle, label: 'Malware Concern', color: 'text-red-500' },
    quality_issue: { icon: Bug, label: 'Quality Issue', color: 'text-yellow-400' },
    metadata_issue: { icon: FileText, label: 'Metadata Issue', color: 'text-blue-400' },
    general: { icon: MessageSquare, label: 'General', color: 'text-gray-400' },
    acknowledgement: { icon: User, label: 'Response', color: 'text-green-400' }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch feedback when panel opens
  useEffect(() => {
    if (isOpen && listingId) {
      fetchFeedback();
    }
  }, [isOpen, listingId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [feedback]);

  const fetchFeedback = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/.netlify/functions/marketplace-admin?action=get_feedback&listing_id=${listingId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load feedback');
      }

      const data = await response.json();
      setFeedback(data.feedback || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendFeedback = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    setError(null);

    try {
      const response = await fetch('/.netlify/functions/marketplace-admin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'send_feedback',
          listingId,
          message: newMessage.trim(),
          feedbackType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send feedback');
      }

      const data = await response.json();
      setFeedback([...feedback, data.feedback]);
      setNewMessage('');
      setFeedbackType('general');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendFeedback();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-md bg-gray-900 border-l border-gray-800 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div>
            <h2 className="text-lg font-semibold text-white">Feedback Thread</h2>
            <p className="text-sm text-gray-400 truncate max-w-xs">{listingTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className="text-center text-red-400 py-8">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
              <p>{error}</p>
              <button
                onClick={fetchFeedback}
                className="mt-2 text-blue-400 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : feedback.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="h-8 w-8 mx-auto mb-2" />
              <p>No feedback yet</p>
              <p className="text-sm mt-1">Send a message to start the conversation</p>
            </div>
          ) : (
            feedback.map((msg) => {
              const isAdmin = msg.sender_role === 'admin';
              const typeConfig = feedbackTypeConfig[msg.feedback_type] || feedbackTypeConfig.general;
              const TypeIcon = typeConfig.icon;

              return (
                <div
                  key={msg.id}
                  className={`flex flex-col ${isAdmin ? 'items-end' : 'items-start'}`}
                >
                  {/* Sender label */}
                  <div className={`flex items-center gap-1 mb-1 text-xs ${isAdmin ? 'text-orange-400' : 'text-blue-400'}`}>
                    {isAdmin ? (
                      <>
                        <Shield className="h-3 w-3" />
                        <span>Admin</span>
                        {msg.feedback_type && msg.feedback_type !== 'general' && (
                          <span className={`ml-1 ${typeConfig.color}`}>
                            ({typeConfig.label})
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <User className="h-3 w-3" />
                        <span>Author</span>
                      </>
                    )}
                  </div>

                  {/* Message bubble */}
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3 ${
                      isAdmin
                        ? 'bg-orange-600/20 border border-orange-500/30 text-white'
                        : 'bg-gray-800 text-white'
                    }`}
                  >
                    {isAdmin && msg.feedback_type && msg.feedback_type !== 'general' && (
                      <div className={`flex items-center gap-1 mb-2 ${typeConfig.color}`}>
                        <TypeIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{typeConfig.label}</span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap">{msg.message}</p>
                  </div>

                  {/* Timestamp */}
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                    <Clock className="h-3 w-3" />
                    <span>{formatDate(msg.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-800">
          {/* Feedback type selector */}
          <div className="flex gap-2 mb-2 flex-wrap">
            {Object.entries(feedbackTypeConfig)
              .filter(([key]) => key !== 'acknowledgement')
              .map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setFeedbackType(key)}
                    className={`flex items-center gap-1 px-2 py-1 text-xs rounded-full border transition-colors ${
                      feedbackType === key
                        ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                        : 'border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    <span>{config.label}</span>
                  </button>
                );
              })}
          </div>

          <div className="flex gap-2">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Send additional feedback to author..."
              rows={2}
              className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder-gray-500"
            />
            <button
              onClick={sendFeedback}
              disabled={sending || !newMessage.trim()}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminFeedbackPanel;
